import { useEffect, useState } from "react";

import { CircularProgress } from "@mui/material";
import { Navigate, useSearchParams } from "react-router-dom";

import { useWriterComponentState } from "hooks";

import { VerifyFailure } from "./components/VerifyFailure";
import { VerifySuccess } from "./components/VerifySuccess";

interface VerifyResponse {
  message: string;
  email: string;
}

export function Verify() {
  const [searchParams] = useSearchParams();

  const [userEmail, setUserEmail] = useState<string | null>(null);

  const userUUID = searchParams.get("user_uuid");
  const code = searchParams.get("code");

  const {
    loading,
    error,
    success,
    write: verifyAccount,
  } = useWriterComponentState({
    writeConfig: {
      method: "post",
      url: "/verify",
      body: {
        user_uuid: userUUID,
        code,
      },
    },
    unauthenticatedRequest: true,
    errorMessage: "Failed to verify your account.",
    errorMessageKey: "verify",
  });

  // Fire request on mount
  useEffect(() => {
    if (userUUID && code) {
      verifyAccount<VerifyResponse>((data) => {
        setUserEmail(data.email);
      });
    }

    // Issue with verifyAccount function re-rendering, so disable exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If there is no UUID or code in url params, return to login
  if (!userUUID || !code) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return <CircularProgress data-testid="verify-loading" />;
  }

  if (error || !success) {
    return <VerifyFailure userUUID={userUUID} verifyError={error} />;
  }

  return <VerifySuccess email={userEmail} />;
}
